import { ChangeEvent, useCallback, useState } from "react";
import {
  BranchFormErrors,
  BranchFormField,
  BranchFormValues,
  isBranchFormField,
  normalizeFieldInput,
  validateBranchForm,
} from "./branchForm";

export type BranchFieldChangeEvent = ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>;

/**
 * Form state shared by the create dialog and the edit form. Inputs carry the field as their
 * `name`, so one handler serves every field.
 */
export function useBranchForm(initialValues: BranchFormValues) {
  const [values, setValues] = useState<BranchFormValues>(initialValues);
  const [errors, setErrors] = useState<BranchFormErrors>({});

  const handleFieldChange = useCallback((event: BranchFieldChangeEvent) => {
    const { name, value } = event.target;
    if (!isBranchFormField(name)) return;
    setValues((prev) => ({ ...prev, [name]: normalizeFieldInput(name, value) }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  }, []);

  const reset = useCallback((nextValues: BranchFormValues) => {
    setValues(nextValues);
    setErrors({});
  }, []);

  /** Shows the messages for `fields` and answers whether the form may be sent. */
  const validate = (fields: readonly BranchFormField[]) => {
    const nextErrors = validateBranchForm(values, fields);
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  return { values, errors, setErrors, handleFieldChange, reset, validate };
}
